import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ShieldCheck, Users, Wine, Share2, Heart, Search, CheckCircle2, XCircle, Download, Mail, Phone } from "lucide-react";
import { toast } from "sonner";

const PLANS = [
  { value: "free", label: "Gratuito" },
  { value: "mensual", label: "Mensual" },
  { value: "semestral", label: "Semestral" },
  { value: "anual", label: "Anual" },
];

function Metric({ icon: Icon, label, value }) {
  return (
    <Card className="p-5 border-grises-200 rounded-xl" data-testid={`metric-${label}`}>
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-lg bg-borgona-50 flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-borgona-700" />
        </span>
        <div>
          <div className="font-mono text-2xl font-bold text-negro">{value}</div>
          <div className="text-xs text-grises-500">{label}</div>
        </div>
      </div>
    </Card>
  );
}

function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("es-ES"); } catch { return d; }
}

export default function Admin() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");

  const { data: metrics } = useQuery({
    queryKey: ["admin-metrics"],
    queryFn: async () => (await api.get("/admin/metrics")).data,
    enabled: user?.is_admin,
  });

  const { data: users, isLoading } = useQuery({
    queryKey: ["admin-users", search],
    queryFn: async () => (await api.get("/admin/users", { params: { search: search || undefined } })).data,
    enabled: user?.is_admin,
  });

  const { data: leads } = useQuery({
    queryKey: ["admin-leads"],
    queryFn: async () => (await api.get("/admin/leads")).data,
    enabled: user?.is_admin,
  });

  const exportLeads = async () => {
    try {
      const res = await api.get("/admin/leads/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "leads-vinonotas.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo exportar"); }
  };

  const setSub = useMutation({
    mutationFn: async ({ id, plan }) => (await api.put(`/admin/users/${id}/subscription`, { plan })).data,
    onSuccess: () => {
      toast.success("Suscripción actualizada");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      qc.invalidateQueries({ queryKey: ["admin-metrics"] });
    },
    onError: () => toast.error("No se pudo actualizar la suscripción"),
  });

  if (user && !user.is_admin) return <Navigate to="/app" replace />;

  return (
    <div className="animate-fade-up" data-testid="admin-page">
      <div className="flex items-center gap-3 mb-2">
        <ShieldCheck className="w-7 h-7 text-borgona-700" strokeWidth={1.75} />
        <h1 className="font-serif text-4xl text-negro">Panel de Administración</h1>
      </div>
      <p className="text-grises-500 mb-8">Gestiona usuarios, suscripciones y métricas de la plataforma.</p>

      {/* Métricas */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-10">
        <Metric icon={Users} label="Usuarios" value={metrics?.total_users ?? "—"} />
        <Metric icon={CheckCircle2} label="Suscripciones activas" value={metrics?.active_subscriptions ?? "—"} />
        <Metric icon={Wine} label="Catas registradas" value={metrics?.total_tastings ?? "—"} />
        <Metric icon={Share2} label="Catas públicas" value={metrics?.public_tastings ?? "—"} />
        <Metric icon={Wine} label="Vinos" value={metrics?.total_wines ?? "—"} />
        <Metric icon={Heart} label="Reacciones" value={metrics?.total_reactions ?? "—"} />
      </div>

      {/* Top usuarios */}
      {metrics?.top_users?.length > 0 && (
        <Card className="p-5 border-grises-200 rounded-xl mb-10">
          <h2 className="font-serif text-xl text-negro mb-4">Usuarios más activos</h2>
          <div className="space-y-2">
            {metrics.top_users.map((u, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-negro">{i + 1}. {u.name} <span className="text-grises-400">· {u.email}</span></span>
                <span className="font-mono font-semibold text-borgona-700">{u.tastings} catas</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Gestión de usuarios */}
      <Card className="p-5 border-grises-200 rounded-xl">
        <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
          <h2 className="font-serif text-xl text-negro">Gestión de usuarios</h2>
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 text-grises-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="admin-user-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre o email"
              className="pl-9 h-10"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-grises-500 border-b border-grises-200">
                <th className="py-2 pr-4 font-medium">Usuario</th>
                <th className="py-2 pr-4 font-medium">Rol</th>
                <th className="py-2 pr-4 font-medium">Catas</th>
                <th className="py-2 pr-4 font-medium">Estado</th>
                <th className="py-2 pr-4 font-medium">Vence</th>
                <th className="py-2 pr-4 font-medium">Suscripción</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={6} className="py-6 text-grises-400">Cargando...</td></tr>}
              {users?.map((u) => (
                <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-b border-grises-100 last:border-0">
                  <td className="py-3 pr-4">
                    <div className="font-medium text-negro">{u.name || "—"}</div>
                    <div className="text-grises-400 text-xs">{u.email}</div>
                  </td>
                  <td className="py-3 pr-4">
                    {u.role === "admin"
                      ? <span className="text-xs px-2 py-0.5 rounded-full bg-borgona-700 text-white">Admin</span>
                      : <span className="text-xs text-grises-500">Catador</span>}
                  </td>
                  <td className="py-3 pr-4 font-mono">{u.tasting_count}</td>
                  <td className="py-3 pr-4">
                    {u.active
                      ? <span className="inline-flex items-center gap-1 text-green-600"><CheckCircle2 className="w-4 h-4" /> Activa</span>
                      : <span className="inline-flex items-center gap-1 text-grises-400"><XCircle className="w-4 h-4" /> {u.status === "vencida" ? "Vencida" : "—"}</span>}
                  </td>
                  <td className="py-3 pr-4 text-grises-500">{fmtDate(u.subscription_end)}</td>
                  <td className="py-3 pr-4">
                    <Select value={u.plan} onValueChange={(plan) => setSub.mutate({ id: u.id, plan })}>
                      <SelectTrigger data-testid={`admin-plan-select-${u.id}`} className="h-9 w-36"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PLANS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Leads — Guía de cata */}
      <Card className="p-5 border-grises-200 rounded-xl mt-10" data-testid="admin-leads-card">
        <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
          <div>
            <h2 className="font-serif text-xl text-negro">Leads · Guía gratuita</h2>
            <p className="text-sm text-grises-500">Contactos capturados con la descarga del folleto de cata.</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-grises-500">Total: <strong className="text-negro font-mono">{leads?.length ?? 0}</strong></span>
            <button
              data-testid="admin-export-leads-btn"
              onClick={exportLeads}
              disabled={!leads?.length}
              className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white bg-borgona-700 hover:bg-borgona-900 transition-colors disabled:opacity-50"
            >
              <Download className="w-4 h-4" /> Exportar CSV
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-grises-500 border-b border-grises-200">
                <th className="py-2 pr-4 font-medium">Nombre</th>
                <th className="py-2 pr-4 font-medium">Correo</th>
                <th className="py-2 pr-4 font-medium">Celular</th>
                <th className="py-2 pr-4 font-medium">Descargas</th>
                <th className="py-2 pr-4 font-medium">Fecha</th>
              </tr>
            </thead>
            <tbody>
              {(!leads || leads.length === 0) && (
                <tr><td colSpan={5} className="py-6 text-grises-400">Aún no hay leads. Aparecerán cuando alguien descargue la guía.</td></tr>
              )}
              {leads?.map((l) => (
                <tr key={l.id} data-testid={`admin-lead-row-${l.id}`} className="border-b border-grises-100 last:border-0">
                  <td className="py-3 pr-4 font-medium text-negro">{l.name || "—"}</td>
                  <td className="py-3 pr-4 text-grises-600"><span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-grises-400" />{l.email}</span></td>
                  <td className="py-3 pr-4 text-grises-600"><span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-grises-400" />{l.phone}</span></td>
                  <td className="py-3 pr-4 font-mono">{l.downloads || 0}</td>
                  <td className="py-3 pr-4 text-grises-500">{fmtDate(l.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
